// Hook pour récupérer et gérer les messages système en temps réel
import { useState, useEffect, useCallback } from 'react';
import {
  collection,
  query,
  where,
  orderBy,
  limit,
  onSnapshot,
  doc,
  setDoc,
  serverTimestamp,
  getDocs,
  writeBatch,
  Timestamp,
} from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../contexts/AuthContext';

const useSystemMessages = () => {
  const { user } = useAuth();
  const [messages, setMessages] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [readStatus, setReadStatus] = useState({});

  // Charger les statuts de lecture depuis Firestore
  const loadReadStatus = useCallback(async (userId) => {
    if (!userId) return {};
    try {
      const statusRef = collection(db, 'users', userId, 'messageStatus');
      const snap = await getDocs(statusRef);
      const status = {};
      snap.forEach((d) => {
        status[d.id] = d.data();
      });
      return status;
    } catch (err) {
      console.error('Erreur chargement statuts messages:', err);
      return {};
    }
  }, []);

  useEffect(() => {
    if (!user?.uid) {
      setLoading(false);
      return;
    }

    setLoading(true);
    const now = Timestamp.now();

    // Filtre : ne montrer que les messages publiés APRÈS la création du compte
    const userCreatedAt = user.createdAt instanceof Timestamp
      ? user.createdAt
      : user.createdAt?.toDate
        ? Timestamp.fromDate(user.createdAt.toDate())
        : user.createdAt
          ? Timestamp.fromDate(new Date(user.createdAt))
          : null;

    // Requête : messages publiés (publishedAt <= maintenant), triés par date desc
    // Le filtre expiresAt est fait côté client pour éviter l'index composite
    const queryConstraints = [
      where('publishedAt', '<=', now),
      orderBy('publishedAt', 'desc'),
      limit(50),
    ];

    if (userCreatedAt) {
      queryConstraints.unshift(where('publishedAt', '>=', userCreatedAt));
    }

    const msgsQuery = query(collection(db, 'systemMessages'), ...queryConstraints);

    const unsubscribe = onSnapshot(
      msgsQuery,
      async (snap) => {
        // Charger les statuts de lecture
        const status = await loadReadStatus(user.uid);
        setReadStatus(status);

        const userSubscription = user.subscription_type || 'free';
        const nowDate = new Date();

        // Filtrer : audience + non expirés
        const filteredMessages = snap.docs
          .map((d) => ({ id: d.id, ...d.data() }))
          .filter((msg) => {
            // Filtre expiration côté client
            if (msg.expiresAt) {
              const expiresDate = msg.expiresAt?.toDate?.() || new Date(msg.expiresAt);
              if (expiresDate < nowDate) return false;
            }
            // Filtre audience
            const audience = msg.targetAudience || 'all';
            return audience === 'all' || audience === userSubscription;
          })
          .slice(0, 25); // Limiter à 25 après filtrage

        setMessages(filteredMessages);

        // Compter les messages non lus
        const unread = filteredMessages.filter((msg) => !status[msg.id]?.read).length;
        setUnreadCount(unread);
        setLoading(false);
      },
      (err) => {
        console.error('Erreur listener messages système:', err);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [user?.uid, user?.subscription_type, loadReadStatus]);

  // Marquer un message comme lu
  const markAsRead = useCallback(
    async (messageId) => {
      if (!user?.uid || !messageId) return;
      // Vérifier si déjà lu pour éviter les écritures inutiles
      if (readStatus[messageId]?.read) return;

      try {
        const statusRef = doc(db, 'users', user.uid, 'messageStatus', messageId);
        await setDoc(
          statusRef,
          {
            read: true,
            readAt: serverTimestamp(),
            messageId,
          },
          { merge: true }
        );

        setReadStatus((prev) => ({ ...prev, [messageId]: { read: true } }));
        setUnreadCount((prev) => Math.max(0, prev - 1));
      } catch (err) {
        console.error('Erreur marquage message comme lu:', err);
      }
    },
    [user?.uid, readStatus]
  );

  // Marquer tous les messages comme lus
  const markAllAsRead = useCallback(async () => {
    if (!user?.uid) return;
    try {
      const batch = writeBatch(db);
      const unreadMessages = messages.filter((msg) => !readStatus[msg.id]?.read);

      if (unreadMessages.length === 0) return;

      unreadMessages.forEach((msg) => {
        const statusRef = doc(db, 'users', user.uid, 'messageStatus', msg.id);
        batch.set(
          statusRef,
          {
            read: true,
            readAt: serverTimestamp(),
            messageId: msg.id,
          },
          { merge: true }
        );
      });

      await batch.commit();

      const newStatus = { ...readStatus };
      unreadMessages.forEach((msg) => {
        newStatus[msg.id] = { read: true };
      });
      setReadStatus(newStatus);
      setUnreadCount(0);
    } catch (err) {
      console.error('Erreur marquage tout lu:', err);
    }
  }, [user?.uid, messages, readStatus]);

  // Rafraîchir les statuts de lecture
  const refreshMessages = useCallback(async () => {
    if (!user?.uid) return;
    const status = await loadReadStatus(user.uid);
    setReadStatus(status);
    const unread = messages.filter((msg) => !status[msg.id]?.read).length;
    setUnreadCount(unread);
  }, [user?.uid, messages, loadReadStatus]);

  // Retourner les messages enrichis avec isRead
  return {
    messages: messages.map((msg) => ({
      ...msg,
      isRead: readStatus[msg.id]?.read || false,
    })),
    unreadCount,
    loading,
    markAsRead,
    markAllAsRead,
    refreshMessages,
  };
};

export default useSystemMessages;
